/**
 * 部署后无感刷新的运行参数。
 *
 * 刻意**不放进 projectSetting**：projectSetting 会被持久化到 localStorage 的 PROJ__CFG__KEY__
 * （见 src/logics/initAppConfig.ts 的 deepMerge + appStore.setProjectConfig），
 * 新 key 的默认值会被写进存量浏览器，此后修改这里的默认值对它们永不生效。
 * 而这些恰恰是需要按线上反馈反复微调的运维参数，所以用普通常量对象承载。
 *
 * 总开关不在这里，而是构建期常量 __AUTO_REFRESH_ENABLED__（见 vite.config.ts 与 .env.production）——
 * 需要能"不发版改源码"就紧急关掉。
 */

export interface AutoRefreshSetting {
  /** 轮询间隔：探测 /version.json 的兜底节奏（其余触发见 setupAutoRefresh 的触发器） */
  pollMs: number;
  /** 切回前台/重连等触发式检查的最小间隔，防止连续触发打爆探测接口 */
  focusMinIntervalMs: number;
  /** 用户无操作多久算空闲。绝大多数刷新会在这个路径上完成 */
  idleMs: number;
  /** 最长等待上限：从"首次发现新版本"起算，超过即不再等空闲 */
  capMs: number;
  /** 硬阻塞（告警弹窗/语音播报/提交在途）长期持有的兜底阈值，超过即强制清理并刷新 */
  hardBlockMaxMs: number;
  /** 两次刷新之间的最小间隔 */
  reloadCooldownMs: number;
  /** 一个窗口内允许的刷新次数上限 */
  maxReloadPerWindow: number;
  /** reloadLedger 的统计窗口长度 */
  reloadWindowMs: number;
  /** 触发次数闸后的退避时长（期间轮询降频） */
  backoffMs: number;
  /** /version.json 探测超时 */
  fetchTimeoutMs: number;
  /** chunk 加载失败兜底的冷却与次数上限（独立于版本刷新计数） */
  chunkReloadCooldownMs: number;
  chunkMaxAttempts: number;
}

export const AUTO_REFRESH_DEFAULTS: Readonly<AutoRefreshSetting> = Object.freeze({
  pollMs: 5 * 60 * 1000,
  focusMinIntervalMs: 30 * 1000,
  idleMs: 60 * 1000,
  capMs: 30 * 60 * 1000,
  hardBlockMaxMs: 30 * 60 * 1000,
  reloadCooldownMs: 120 * 1000,
  maxReloadPerWindow: 3,
  reloadWindowMs: 30 * 60 * 1000,
  backoffMs: 30 * 60 * 1000,
  fetchTimeoutMs: 5 * 1000,
  chunkReloadCooldownMs: 30 * 1000,
  chunkMaxAttempts: 2,
});

/** 调试覆盖用的 localStorage key：让 QA/运维不重新构建就能把 poll 缩到 5s 验证 */
const OVERRIDE_KEY = '__AR__OVERRIDE__';

/**
 * 读取调试覆盖。
 *
 * 刻意**不做环境判断**：这个功能本身只在生产构建里跑（__AUTO_REFRESH_ENABLED__），
 * 若只在非生产构建生效，覆盖机制就永远用不上，等于没有。
 * 代价是存量浏览器上可能残留一条覆盖，所以一旦生效就打 warn 让人看得见。
 */
function readOverride(): Partial<AutoRefreshSetting> {
  try {
    const raw = window.localStorage?.getItem(OVERRIDE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    const override: Recordable<number> = {};
    Object.keys(AUTO_REFRESH_DEFAULTS).forEach((key) => {
      // 只接受正数，避免把 pollMs 覆盖成 0 造成死循环探测
      const value = Number(parsed[key]);
      if (Number.isFinite(value) && value > 0) {
        override[key] = value;
      }
    });
    if (Object.keys(override).length > 0) {
      console.warn(
        '[auto-refresh] 参数被调试覆盖生效（仅用于验证，请清理 localStorage）',
        override,
      );
    }
    return override;
  } catch {
    return {};
  }
}

/**
 * 取当前生效的参数。
 * 每轮判定都会调用，代价是一次 JSON.parse，可忽略。
 */
export function getAutoRefreshSetting(): AutoRefreshSetting {
  return { ...AUTO_REFRESH_DEFAULTS, ...readOverride() };
}
